import React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { usePartnerContent } from '@/hooks/usePartnerContent';
import { PageLoading } from '@/features/static/shared';
import { PartnerHero } from './components/PartnerHero';
import { PartnerFeatureCard } from './components/PartnerFeatureCard';
import { PARTNER_COLORS } from './constants';
import { Store, TrendingUp, Sparkles } from 'lucide-react';

const CARD_ICONS = [Store, TrendingUp, Sparkles];

export const BliPartnerPage = () => {
  const { settings, loading, mailto } = usePartnerContent();
  const bli = settings?.bli_partner || {};

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: PARTNER_COLORS.bg }}>
        <PageLoading />
      </div>
    );
  }

  const offerCards = (bli.offer_cards || []).slice(0, 3);

  return (
    <div className="min-h-screen" style={{ backgroundColor: PARTNER_COLORS.bg }} data-testid="bli-partner-page">
      <SeoMeta
        title="Bli partner med ByggFynd"
        description={bli.hero_subtitle || 'ByggFynd samarbetar med etablerade byggvaruhus.'}
        canonicalPath="/bli-partner"
      />

      <PartnerHero
        title={bli.hero_title}
        subtitle={bli.hero_subtitle}
        heroImageUrl={bli.hero_image_url}
        primaryLabel={bli.hero_button_label}
        primaryHref="/bli-partner/anslut-byggvaruhus"
      />

      <div className="container-main py-4">
        <Breadcrumbs items={[{ label: 'Bli partner', href: '/bli-partner' }]} />
      </div>

      <section className="container-main max-w-3xl mx-auto px-4 pb-16 md:pb-20">
        <div className="border-t border-[#E5E7EB] pt-12 md:pt-16">
          <h2 className="text-2xl md:text-3xl font-black mb-4" style={{ color: PARTNER_COLORS.navy }}>
            {bli.who_title}
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: PARTNER_COLORS.textMuted }}>
            {bli.who_text}
          </p>
        </div>
      </section>

      <section className="border-t border-[#E5E7EB] bg-white py-16 md:py-20">
        <div className="container-main max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-12" style={{ color: PARTNER_COLORS.navy }}>
            {bli.offer_title}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {offerCards.map((card, i) => (
              <PartnerFeatureCard
                key={card.title || i}
                icon={CARD_ICONS[i] || Store}
                title={card.title}
                description={card.description}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="container-main max-w-3xl mx-auto px-4 py-16 md:py-20 text-center border-t border-[#E5E7EB]">
        <h2 className="text-2xl md:text-3xl font-black mb-4" style={{ color: PARTNER_COLORS.navy }}>
          {bli.cta_title}
        </h2>
        <p className="text-lg mb-8" style={{ color: PARTNER_COLORS.textMuted }}>
          {bli.cta_text}
        </p>
        <Link
          to="/bli-partner/anslut-byggvaruhus"
          className="inline-flex items-center justify-center rounded-full px-10 py-4 text-base font-bold text-white shadow-md transition-all hover:shadow-lg"
          style={{ backgroundColor: PARTNER_COLORS.accent }}
        >
          {bli.cta_button_label}
        </Link>
        <p className="mt-6 text-sm" style={{ color: PARTNER_COLORS.textMuted }}>
          Eller{' '}
          <a href={mailto} className="font-semibold underline" style={{ color: PARTNER_COLORS.accent }}>
            kontakta oss direkt
          </a>
        </p>
      </section>
    </div>
  );
};

export default BliPartnerPage;
